// Small shared UI helpers for the FIN-ARON feature.

export { InfoDot } from "@/features/power_projects/advisor/labels";

import type { Variant } from "./types";

/** Teksty dymków „i" — biznesowe wyjaśnienia metryk FIN-ARON w jednym miejscu. */
export const HELP = {
  inflow:
    "Wpływy = wartość projektu (Project.total_value) rozliczana w 100% w PROGNOZOWANEJ dacie zakończenia (wybrany wariant prognozy DAG-u kamieni). Nie fakturowo.",
  variants:
    "Optymistyczny = SLA ustawowe ×1,0 · Neutralny = ×1,5 lub mediana historyczna spraw · Pesymistyczny = ×2,2 lub p90. Liczone po ścieżce krytycznej procesu.",
  salary:
    "Pensja do końca = koszt pracodawcy (EmployeeSalaryCost, prorata + skala z realnych wypłat) × udział koordynatora na projekcie, miesiąc po miesiącu od dziś do prognozowanego końca. Bieżący miesiąc: fakty alokacji (PEA), jeśli są; pozostałe miesiące: udział ZE koordynatora (suwak, default 100%) dzielony równo na jego projekty ZE.",
  overhead:
    "Narzut kosztorysowy = pensja przypisana projektowi × narzut %. Narzut % = auto-propozycja (pula: pensje NIE-koordynatorów + koszty wewnętrzne NIEPRZYPISANE do przedsięwzięć ÷ baza: pensje koordynatorów × ich udział ZE) lub ręczne nadpisanie.",
  overheadFuture:
    "Miesiące przyszłe nie mają danych — auto-propozycja dla przyszłości to SUGESTIA: średnia z ostatnich miesięcy okna, kotwiczona na miesiącu bieżącym.",
  subcontractor:
    "Koszty podwykonawcze warstwami, najtwardsza wygrywa (bez podwójnego liczenia): faktura > zamówienie > zatwierdzone zapotrzebowanie > SZKIC zapotrzebowania. Wszystkie te warstwy WCHODZĄ do marży (szkic = najmiększy szacunek). Koszty zewnętrzne — osobno, miękko.",
  margin:
    "Marża = wpływy − podwykonawcy (twarde) − pensja do końca − narzut. To PROGNOZOWANY zysk na portfelu koordynatora.",
  marginPct: "Marża % = marża ÷ wpływy. Rentowność portfela koordynatora przy założeniu, że robi tylko te projekty.",
  marginPerMonth: "Marża ÷ liczba miesięcy do najpóźniejszego prognozowanego końca (horyzont).",
  returnOnCost: "Ile złotych marży generuje każda 1 zł kosztu całkowitego (podwykonawcy + pensja + narzut). Wartość ujemna = strata: np. −0,86 znaczy, że każda wydana złotówka przynosi 86 gr straty (wpływy nie pokrywają kosztów).",
  progress: "Średni % ukończonych kamieni milowych po projektach koordynatora (bez „nie dotyczy”).",
  cashflow:
    "Symulacja jak w finansach GK: spływy projektów w prognozowanych datach (+, klik → projekt), faktury podwykonawcze w miesiącu faktury (fakt, −), otwarte zamówienia/zapotrzebowania/szkice w dacie końca projektu (−, klik → dokument), pensje i narzut miesięcznie (−). Koszty stałe firmy siedzą w narzucie — proporcjonalnie do pensji przypisanej projektom.",
  quarters: "Zsumowane eventy symulacji od bieżącego kwartału w przód: wpływy, wydatki, netto i wynik narastająco.",
  inflows:
    "Termin umowy vs trzy prognozowane daty ukończenia. Poślizg = prognoza neutralna − termin umowy (dni). Status liczony względem terminu umowy.",
  slip: "Prognoza neutralna minus termin umowy, w dniach. Dodatni = po terminie.",
  pace: "Kroki procesu pchnięte do przodu na dzień DOSTĘPNY (po odjęciu urlopów/L4).",
  returns: "Mediana dni, przez które pozycja czekała po stronie koordynatora, zanim ją pchnął dalej („odbicie piłeczki”).",
  h2h:
    "Porównanie skuteczności WŁASNEJ pracy: marża liczona BEZ kosztów podwykonawczych (wpływy − pensja − narzut) — mierzy koordynatora, nie jego łańcuch podwykonawców. Klasyczna marża obok, informacyjnie.",
  zeShare:
    "Udział etatu koordynatora w projektach ZE w danym miesiącu — DEFAULT 100%, edytowalny suwakiem (zapis w bazie per miesiąc); miesiące przed startem ZE (do 06.2026) = 0%. Skaluje bazę narzutu i pensję w marżach; reszta etatu jest poza analizą FIN-ARON. Nie wnioskujemy udziału z alokacji/prognoz — tylko jawny zapis.",
  overheadBase:
    "Baza = pensje koordynatorów ZE × ich udział ZE. Pula WSPÓLNA = pensje NIE-projektowych (administracja itd.) + koszty wewnętrzne nieprzypisane do przedsięwzięć. Do narzutu ZE trafia z niej tylko część PROPORCJONALNA do udziału bazy ZE w pensjach wszystkich ludzi projektowych — np. gdy ZE to 25% kosztów ludzi, inni projektowi 50%, reszta firmy 25%: pula ZE = 25% × 1/3. Ludzie projektowi spoza ZE NIE są narzutem ZE (mają własne projekty).",
  projectStatus:
    "Projektowy = pracuje na projektach (jego pensja NIE jest narzutem — obciąża własne projekty). Nieprojektowy = obsługa firmy (kadry/księgowość/zarząd) — pensja wchodzi do puli wspólnej narzutu. Default z profilu użytkownika; nadpisanie per miesiąc obowiązuje OD tego miesiąca aż do następnego zapisu (carry-forward). Ten sam mechanizm co w finansach GK.",
} as const;

export function fmtPln(v: string | null | undefined): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("pl-PL", { maximumFractionDigits: 0 }) + " zł";
}

export function marginTone(v: string | null | undefined): "danger" | "ok" | "default" {
  if (v == null) return "default";
  const n = Number(v);
  if (Number.isNaN(n)) return "default";
  return n < 0 ? "danger" : "ok";
}

const VARIANTS: { key: Variant; label: string }[] = [
  { key: "optimistic", label: "Optymistyczny" },
  { key: "neutral", label: "Neutralny" },
  { key: "pessimistic", label: "Pesymistyczny" },
];

export function VariantSwitch({
  value,
  onChange,
}: {
  value: Variant;
  onChange: (v: Variant) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-[var(--color-border)] overflow-hidden text-xs">
      {VARIANTS.map((v) => (
        <button
          key={v.key}
          onClick={() => onChange(v.key)}
          className={`px-3 py-1.5 font-medium transition-colors ${
            value === v.key
              ? "bg-sky-600 text-white"
              : "bg-transparent text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10"
          }`}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

export const FLAG_LABELS: Record<string, string> = {
  no_value: "brak wartości projektu",
  no_salary_data: "brak danych o pensji",
  forecast_pending: "prognoza w przygotowaniu",
  overdue: "prognoza po terminie",
};

export function fmtPct(v: string | number | null | undefined, digits = 1): string {
  if (v == null) return "—";
  const n = Number(v);
  return Number.isNaN(n) ? "—" : `${n.toFixed(digits)}%`;
}

export function SlipBadge({ days }: { days: number | null }) {
  if (days == null) return <span className="text-gray-400">—</span>;
  const cls =
    days > 30
      ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
      : days > 0
        ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
        : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
  return (
    <span className={`inline-block text-[11px] font-semibold rounded px-1.5 py-0.5 tabular-nums ${cls}`}>
      {days > 0 ? `+${days} dni` : `${days} dni`}
    </span>
  );
}

export function ProgressBar({ pct, label }: { pct: number | null; label?: string }) {
  if (pct == null) return <span className="text-xs text-gray-400">—</span>;
  const tone = pct >= 70 ? "bg-emerald-500" : pct >= 35 ? "bg-sky-500" : "bg-amber-500";
  return (
    <span className="inline-flex items-center gap-1.5 min-w-24">
      <span className="h-1.5 w-16 rounded bg-black/10 dark:bg-white/10 overflow-hidden shrink-0">
        <span className={`block h-full ${tone}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </span>
      <span className="text-xs tabular-nums text-gray-600 dark:text-gray-300">
        {pct}%{label ? ` ${label}` : ""}
      </span>
    </span>
  );
}

const DEADLINE_CLS: Record<string, string> = {
  on_track: "text-emerald-600 dark:text-emerald-400",
  at_risk: "text-amber-600 dark:text-amber-400",
  will_miss: "text-red-600 dark:text-red-400",
  unknown: "text-gray-400",
};

const DEADLINE_LABEL: Record<string, string> = {
  on_track: "zdąży",
  at_risk: "ryzyko",
  will_miss: "nie zdąży",
  unknown: "—",
};

export function DeadlineFlag({ flag }: { flag: string }) {
  return (
    <span className={`text-xs font-semibold ${DEADLINE_CLS[flag] ?? DEADLINE_CLS.unknown}`}>
      {DEADLINE_LABEL[flag] ?? flag}
    </span>
  );
}
